<!--
The base button component for the Giftly portal.

All components in the consumer portal should be named with a G (Giftly) prefix.
-->
<script lang="ts" setup>import { computed as _computed } from 'vue';
const explicitVersion = __MACROS_toRef(__props, "version");
const explicitIconSize = __MACROS_toRef(__props, "iconSize");
const explicitVariant = __MACROS_toRef(__props, "variant");
const explicitSize = __MACROS_toRef(__props, "size");

import { toRef as __MACROS_toRef } from "vue";
import GLink from '@consumer/components/GLink.vue'
import { isProduction } from '@shared/environment'
import { type IconSize, iconSizeForButton, loadingIconSizeForButton } from '@consumer/logic/icons'
import type { IconDefinition } from '@fortawesome/pro-light-svg-icons'

export type ButtonSize = 'xsmall' | 'small' | 'medium' | 'large' | 'inherit'
export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'gold' | 'transparent' | 'flat' | 'overlay'

const injectedVersion = inject<number>('gButtonVersion', undefined!)

withDefaults(defineProps<{
  as?: any
  color?: 'gold' | 'transparent'
  danger?: boolean
  disabled?: boolean
  flat?: boolean
  ghost?: boolean
  href?: string
  icon?: string | IconDefinition
  iconRight?: string | IconDefinition
  label?: string
  name?: string
  primary?: boolean
  processing?: boolean
  variant?: ButtonVariant
  rel?: string
  round?: boolean
  pill?: boolean
  v2?: boolean
  version?: number
  secondary?: boolean
  size?: ButtonSize
  iconSize?: IconSize
  tertiary?: boolean
}>(), {  })

const size = _computed(() => __props.size ?? (__props.flat ? 'xsmall' : 'large'))
const componentAs = _computed(() => __props.as ?? (__props.href ? GLink : 'button'))

const variant = _computed(() => {
  if (__props.variant) return __props.variant
  if (__props.color) return __props.color
  if (__props.secondary) return 'secondary'
  if (__props.tertiary) return 'tertiary'
  if (__props.flat) return 'flat'
  if (!__props.primary && !isProduction) {
    console.error('Please specify the button variant explicitly for ', __props.label || __props.name)
  }
  return 'primary'
})

const version = _computed(() => __props.version ?? unref(injectedVersion))
const isV2 = _computed(() => __props.v2 || version.value === 2)
const iconSize = _computed(() => __props.iconSize || iconSizeForButton(size.value, __props.flat))

const buttonRef = ref()
defineExpose({
  $el: buttonRef,
  focus: () => buttonRef.value?.focus(),
  blur: () => buttonRef.value?.blur(),
  click: () => buttonRef.value?.click(),
})
</script>

<template>
  <component
    :is="componentAs"
    ref="buttonRef"
    :type="href ? undefined : 'button'"
    :name="name || label"
    :href="href"
    class="g-button"
    :class="[
      `g-button-${size}`,
      flat ? '' : `g-button-${variant}`,
      {
        disabled,
        processing,
        'g-button-danger': danger,
        'g-button-round': round,
        'g-button-pill': pill,
        'g-button-v2': isV2,
        'g-button-no-label': !(label || $slots.default),
        'g-button-flat': flat,
        'g-button-primary-flat': flat && primary,
        'g-button-secondary-flat': flat && (secondary || (!primary && !tertiary && !explicitVariant)),
        'g-button-secondary-ghost': ghost && variant === 'secondary',
        'g-button-tertiary-flat': flat && tertiary
      },
    ]"
    :disabled="disabled || undefined"
    :rel="rel"
  >
    <LoadingIndicator v-if="processing" :size="loadingIconSizeForButton(size, flat)"/>
    <slot v-else>
      <GIcon v-if="icon" :icon="icon" :size="iconSize"/>
      <slot name="customIcon"/>
      <template v-if="label">{{ label }}</template>
      <slot name="addonRight">
        <GIcon v-if="iconRight" :icon="iconRight" :size="iconSize"/>
      </slot>
    </slot>
  </component>
</template>

<style lang="scss">
a.g-button:not(.g-button-flat) {
  align-items: center;
  justify-content: center;
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }
}

.g-button {
  @apply flex font-semibold antialiased outline-none;
  @apply justify-center items-center gap-2;
  @apply truncate;
  @apply shadow-v2sm rounded-v2md;

  --height: 45px;

  background: var(--bg);
  border-color: var(--br, var(--bg));
  color: var(--color);
  fill: currentcolor;
  font-size: 18px;
  height: var(--height);
  stroke: currentcolor;
  transition: ease 0.5s;

  &:not(:disabled, .disabled) {
    &:hover,
    &:focus,
    &:active {
      --bg: var(--bg-focus);

      color: var(--color-focus, var(--color));
    }

    // TODO: When the time is right, https://caniuse.com/?search=Relative%20Color%20Syntax
    // &:active {
    //   --bg: rgb(from var(--bg-focus) r g b / .8);
    // }
  }

  &.processing {
    @apply cursor-wait;
  }

  &.hover {
    --bg: var(--bg-focus);

    color: var(--color-focus, var(--color));
  }

  &:disabled,
  &.disabled {
    opacity: 0.4;
  }

  .g-icon {
    flex-shrink: 0;
  }
}

.g-button-primary {
  --color: white;
  --bg: theme("colors.primary.base");
  --bg-focus: theme("colors.primary.dark");

  &.g-button-danger {
    --bg: theme("colors.red.base");
    --bg-focus: theme("colors.red.dark");
  }

  &:disabled,
  &.disabled {
    --bg: theme("colors.grey.primary");
    --color: theme("colors.v2-tertiary");

    opacity: 1;
  }
}

.g-button-secondary {
  @apply border border-solid;

  --color: theme("colors.primary.base");
  --bg: white;
  --br: theme("colors.grey.primary");
  --bg-focus: theme("colors.primary.pale");

  &.g-button-danger {
    --color: theme("colors.red.base");
    --bg-focus: theme("colors.red.pale");
  }
}

.g-button-secondary-ghost {
  @apply bg-surface-blue;

  --br: transparent;
  --bg-focus: theme("colors.grey.pale");
}

.g-button-tertiary {
  @apply border border-solid;

  --bg: transparent;
  --br: theme("colors.grey.primary");
  --bg-focus: theme("colors.surface-grey");
  --color: theme("colors.v2-secondary");
  --color-focus: theme("colors.navy");

  &.g-button-danger {
    --bg-focus: theme("colors.red.pale");
    --color-focus: theme("colors.red.base");
  }
}

.g-button-gold {
  --bg: theme("colors.gold");
  --bg-focus: "#E89F1D";
  --color: theme("colors.navy");
}

.g-button-transparent {
  --color: theme("colors.v2-secondary");
  --color-focus: theme("colors.navy");
  --bg-focus: rgb(0 0 0 / 8%);

  border: none;
  box-shadow: none;
  transition-duration: 100ms;

  &.g-button-danger {
    --bg-focus: theme("colors.red.pale");
    --color-focus: theme("colors.red.base");
  }
}

/* Frosted translucent fill for controls layered over imagery (event hero
   header buttons). Shares the fill tokens with OverlayChip — see the Overlay
   component + design_tokens.scss. */
.g-button-overlay {
  --bg: var(--color-bg-overlay-light);
  --bg-focus: var(--color-bg-overlay-light-hover);
  --color: white;

  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
  border: none;
  box-shadow: none;

  &:focus-visible {
    outline: 2px solid rgb(255 255 255 / 80%);
    outline-offset: 2px;
  }
}

/* Flat button style based on Figma Design System */
.g-button-flat {
  @apply gap-1 inline-flex;

  border-radius: 0;
  box-shadow: none;
  height: auto;
  text-underline-offset: 4px;
  transition-duration: 100ms;

  &:not(:disabled, .disabled) {
    &:hover,
    &:focus {
      text-decoration: underline;
    }
  }

  &.g-button-danger {
    --color-focus: theme("colors.red.base");
  }
}

/* Additional flat button styles based on primary/secondary/tertiary attributes */
.g-button-primary-flat {
  --color: theme("colors.primary.base");

  &.g-button-danger {
    --color: theme("colors.v2-orange-rust");
  }
}

.g-button-secondary-flat {
  --color: theme("colors.navy");
  --color-focus: theme("colors.primary.base");

  text-decoration: underline;
}

.g-button-tertiary-flat {
  --color: theme("colors.v2-tertiary");
}

.g-button-inherit {
  font-size: inherit;
}

.g-button-large {
  @apply px-8;

  --height: 56px;

  &.g-button-flat {
    @apply px-0;
  }
}

.g-button-medium {
  @apply px-6;

  --height: 50px;

  font-size: 16px;

  &.g-button-flat {
    @apply px-0;
  }
}

.g-button-small:not(.g-button-tab) {
  @apply px-4;

  font-size: 16px;

  &.g-button-flat {
    @apply px-0;
  }
}

.g-button-xsmall.g-button-flat {
  $padding: 0.75rem;

  font-size: 14px;
  margin-bottom: -$padding;
  margin-top: -$padding;
  padding-bottom: $padding;
  padding-top: $padding;
}

.g-button-tab {
  @apply rounded-v2sm;
  --tw-shadow: 0 3px 8px 0 rgb(17 30 54 / 2%);

  font-size: inherit;
  transition-duration: 100ms;

  &:not(.active) {
    --color: inherit;
  }

  &.active {
    --br: theme("colors.primary.base");
    --bg: theme("colors.primary.pale");

    outline: 1px solid theme("colors.primary.base");
    outline-offset: 0;
  }
}

.g-button.g-button-no-label {
  padding: initial;
  width: var(--height);
}

.g-button-secondary.g-button-no-label {
  --color: theme("colors.v2-tertiary");
  --color-focus: theme("colors.primary.base");
}

.g-button-round {
  @media screen and (max-width: 1023px) {
    --height: 44px;
  }

  --height: 64px;

  border-radius: 100%;
  width: var(--height);
}

.g-button-xsmall.g-button-round {
  @media screen and (max-width: 1023px) {
    --height: 45px;
  }

  --height: 45px;
}

.g-button-pill {
  @apply rounded-full;

  font-weight: 600;
  line-height: 140%;
  min-width: fit-content;
  padding: 8px 16px;
}

.g-button-xsmall.g-button-pill {
  font-size: 13px;
}

.g-button-small.g-button-pill {
  font-size: 16px;
}

.g-button-v2 {
  @apply rounded-full;

  --height: 54px;

  font-size: 16px;
  font-weight: 600;
  line-height: 140%;
}

.g-button.icon-button {
  @apply rounded-full inline-flex outline-none leading-none;
  --p: 0.5rem;
  --height: auto;

  aspect-ratio: 1 / 1;
  margin-bottom: calc(var(--p) * -1);
  margin-top: calc(var(--p) * -1);
  padding: var(--p);

  &.g-button-small {
    --p: 0.6rem;
  }

  &.g-button-medium {
    --p: 0.7rem;
  }

  &.g-button-large {
    --p: 0.8rem;
  }
}
</style>
